/**
 * ClientConfirmSection — client detail + selection header for the
 * combined-prefill pre_launch drawer (analyze → auto-open), shared by every
 * host.
 *
 * Rendered ABOVE the input form via InstanceDetailDrawer's
 * ``renderClientSection`` render-prop (mirrors ``renderInputForm``) on the
 * add-in; ora's `workflows.triage.$messageId` route (story 26752) mounts it
 * directly above `LaunchForm` the same way. Purely props-driven — the HOST
 * owns the client state, never this component; it only renders three states:
 *
 *   resolved client → an informational pick-confidence badge (R2 — launching
 *                     IS the confirmation, so there is NO Confirm button/gate)
 *                     + a "Change" affordance + the shared ClientContextTile
 *                     (full client details: name, location, branch,
 *                     policies-in-force, opportunities — R3, ui/CLAUDE.md
 *                     parity with LaunchTab's pre-launch tile).
 *   no client + suggestions   → "did you mean" picker
 *   no client + no suggestions → shared ClientSearchDropdown
 *
 * Picking a different client (Change / did-you-mean / search) fires
 * ``onSelectClient`` — the host re-runs prefill (add-in) or re-fetches client
 * details (ora) for the newly-chosen client.
 *
 * Moved from add-in-only `src/components/ClientConfirmSection.tsx` to this
 * shared location (story 26752) when ora's triage route became its second
 * consumer — a forked copy is exactly the drift ui/CLAUDE.md bans, and the
 * two hosts rendering visibly different client cards for the same brief was
 * the bug report that prompted the move.
 */
import { useEffect, useState } from "react";
import type { ClientDetails, ClientSearchResult } from "~/common/workflows/types/client";
import { ClientContextTile } from "./ClientContextTile";
import ClientSearchDropdown from "./ClientSearchDropdown";

export interface ClientConfirmSectionProps {
  /** The resolved client (full details), or null when analyze couldn't resolve one. */
  details: ClientDetails | null;
  /** Substrate's self-rated pick certainty; absent on human-picked clients. */
  pickConfidence?: "high" | "medium" | "low";
  /** "Did you mean" candidates from analyze_email (no-client state). */
  suggestions: ClientSearchResult[];
  /** True while the client lookup is still resolving. */
  loading: boolean;
  /** Fires with the FULL picked result (not just clientSk) so the caller can
   *  seed clientName immediately instead of waiting on getClientDetails. */
  onSelectClient: (result: ClientSearchResult) => void;
  /** Async search for the manual-picker state (searchClientsLegacy wrap). */
  searchClients?: (q: string) => Promise<ClientSearchResult[]>;
}

/**
 * Informational pick-confidence pill (R2). Purely a display — NOT an action.
 * green = high, amber = medium, gray = low; a human pick (no substrate rating)
 * shows a neutral "Selected".
 */
function ConfidenceBadge({
  pickConfidence,
}: {
  pickConfidence?: "high" | "medium" | "low";
}) {
  const base =
    "flex-shrink-0 rounded border px-1.5 py-[1px] text-2xs font-medium";
  if (pickConfidence === "high") {
    return (
      <span
        data-testid="client-confidence-badge"
        className={`${base} border-primary-success-200 bg-primary-success-50 text-primary-success-700`}
      >
        High confidence
      </span>
    );
  }
  if (pickConfidence === "medium") {
    return (
      <span
        data-testid="client-confidence-badge"
        className={`${base} border-primary-warning-200 bg-primary-warning-50 text-primary-warning-700`}
      >
        Medium confidence
      </span>
    );
  }
  if (pickConfidence === "low") {
    return (
      <span
        data-testid="client-confidence-badge"
        className={`${base} border-primary-gray-light-200 bg-primary-gray-light-100 text-primary-gray-light-500`}
      >
        Low confidence
      </span>
    );
  }
  // Absent (e.g. human-picked): the client is chosen, just no substrate rating.
  return (
    <span
      data-testid="client-confidence-badge"
      className={`${base} border-primary-gray-light-200 bg-primary-gray-light-100 text-primary-gray-light-500`}
    >
      Selected
    </span>
  );
}

export function ClientConfirmSection({
  details,
  pickConfidence,
  suggestions,
  loading,
  onSelectClient,
  searchClients,
}: ClientConfirmSectionProps) {
  // "Change" toggles the picker open under the client details. Reset whenever a
  // different client resolves (a pick here or the async selectClient landing).
  const [changing, setChanging] = useState(false);
  useEffect(() => {
    setChanging(false);
  }, [details?.client.client_sk]);

  const handlePick = (result: ClientSearchResult) => {
    setChanging(false);
    onSelectClient(result);
  };

  const picker = (heading: string) => (
    <div className="flex flex-col gap-2">
      {suggestions.length > 0 && (
        <div data-testid="client-suggestions">
          <div className="mb-1 text-xs text-primary-gray-light-400">
            {heading}
          </div>
          <div className="overflow-hidden rounded-md border border-primary-gray-light-200">
            {suggestions.map((r, i) => (
              <button
                type="button"
                key={r.clientSk}
                onClick={() => handlePick(r)}
                className={
                  "flex w-full touch-target items-center justify-between gap-2 bg-base-white px-[10px] py-[7px] text-left text-xs hover:bg-primary-gray-light-50 " +
                  (i < suggestions.length - 1
                    ? "border-b border-primary-gray-light-100"
                    : "")
                }
              >
                <span className="min-w-0 truncate font-medium text-primary-gray-light-900">
                  {r.clientName}
                </span>
                {r.branchName && (
                  <span className="flex-shrink-0 text-xs text-primary-gray-light-400">
                    {r.branchName}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
      {suggestions.length === 0 && (
        <div className="text-xs text-primary-gray-light-400">{heading}</div>
      )}
      {searchClients && (
        <ClientSearchDropdown
          searchClients={searchClients}
          onSelect={(result) => handlePick(result)}
        />
      )}
    </div>
  );

  if (loading) {
    return (
      <div
        className="flex items-center gap-2 rounded-lg border border-primary-gray-light-200 bg-primary-gray-light-50 px-3 py-2"
        data-testid="client-confirm-section"
        role="status"
      >
        <span
          className="inline-block h-3.5 w-3.5 flex-shrink-0 animate-spin rounded-full border-2 border-primary-gray-light-300 border-t-primary-gray-light-700"
          aria-hidden="true"
        />
        <p className="min-w-0 text-xs text-primary-gray-light-700">
          Finding the client from this email…
        </p>
        <span className="sr-only">
          Identifying client
        </span>
      </div>
    );
  }

  if (details) {
    return (
      <div className="flex flex-col gap-2" data-testid="client-confirm-section">
        <div className="flex items-center justify-between gap-2">
          <ConfidenceBadge pickConfidence={pickConfidence} />
          <button
            type="button"
            onClick={() => setChanging((c) => !c)}
            data-testid="client-change-button"
            className="inline-flex touch-target items-center text-xs text-primary-gray-light-500 underline hover:text-primary-gray-light-700"
          >
            {changing ? "Cancel" : "Change"}
          </button>
        </div>
        {/* Full client details — reuse the shared ClientContextTile that
            LaunchTab's pre-launch client tile renders (ui/CLAUDE.md parity):
            name, location, branch, policies-in-force, opportunities. */}
        <ClientContextTile
          kind="loaded"
          details={details}
          collapsiblePolicies
        />
        {changing && picker("Select a different client:")}
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-2 rounded-lg border border-primary-gray-light-200 bg-primary-gray-light-25 px-3 py-2"
      data-testid="client-confirm-section"
    >
      {picker(
        suggestions.length > 0
          ? "Could not identify client — did you mean:"
          : "Search for the client this workflow is for:",
      )}
    </div>
  );
}

export default ClientConfirmSection;
